var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef, } from 'react';
import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import styled from '../../styles/styled';
import { typography, focusVisible, hideVisually, } from '../../styles/style-mixins';
import { useComponents } from '../ComponentsContext';
import { useClickEvent } from '../../hooks/useClickEvent';
import Spinner from '../Spinner';
import { AccessibilityError } from '../../util/errors';
const spinnerBaseStyles = ({ theme }) => css `
  position: absolute;
  opacity: 0;
  visibility: hidden;
  transition: opacity ${theme.transitions.default},
    visibility ${theme.transitions.default};
`;
const spinnerLoadingStyles = ({ isLoading }) => isLoading &&
    css `
    opacity: 1;
    visibility: inherit;
  `;
const LoadingIcon = styled(Spinner)(spinnerBaseStyles, spinnerLoadingStyles);
const LoadingLabel = styled.span(hideVisually);
const iconStyles = (theme) => css `
  flex-shrink: 0;
  margin-right: ${theme.spacings.byte};
`;
const contentStyles = ({ theme }) => css `
  display: inline-flex;
  align-items: center;
  opacity: 1;
  visibility: inherit;
  transform: scale3d(1, 1, 1);
  transition: opacity ${theme.transitions.default},
    transform ${theme.transitions.default},
    visibility ${theme.transitions.default};
`;
const contentLoadingStyles = ({ isLoading }) => isLoading &&
    css `
    opacity: 0;
    visibility: hidden;
    transform: scale3d(0, 0, 0);
  `;
const Content = styled.span(contentStyles, contentLoadingStyles);
const baseStyles = ({ theme }) => css `
  display: inline-flex;
  justify-content: center;
  width: auto;
  height: auto;
  margin: 0;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  font-weight: ${theme.fontWeight.bold};
  border-width: ${theme.borderWidth.kilo};
  border-style: solid;
  border-radius: ${theme.borderRadius.pill};
  transition: opacity ${theme.transitions.default},
    color ${theme.transitions.default},
    background-color ${theme.transitions.default},
    border-color ${theme.transitions.default};

  &:disabled,
  &[disabled] {
    pointer-events: none;
  }
`;
const primaryStyles = ({ variant = 'secondary', destructive, }) => {
    if (variant !== 'primary') {
        return null;
    }
    if (destructive) {
        return css `
      background-color: var(--cui-bg-danger-strong);
      border-color: transparent;
      color: var(--cui-fg-on-strong);

      &:hover {
        background-color: var(--cui-bg-danger-strong-hovered);
        border-color: transparent;
        color: var(--cui-fg-on-strong-hovered);
      }

      &:active,
      &[aria-expanded='true'],
      &[aria-pressed='true'] {
        background-color: var(--cui-bg-danger-strong-pressed);
        border-color: transparent;
        color: var(--cui-fg-on-strong-pressed);
      }

      &:disabled,
      &[disabled] {
        background-color: var(--cui-bg-danger-strong-disabled);
        border-color: transparent;
        color: var(--cui-fg-on-strong-disabled);
      }
    `;
    }
    return css `
    background-color: var(--cui-bg-accent-strong);
    border-color: transparent;
    color: var(--cui-fg-on-strong);

    &:hover {
      background-color: var(--cui-bg-accent-strong-hovered);
      border-color: transparent;
      color: var(--cui-fg-on-strong-hovered);
    }

    &:active,
    &[aria-expanded='true'],
    &[aria-pressed='true'] {
      background-color: var(--cui-bg-accent-strong-pressed);
      border-color: transparent;
      color: var(--cui-fg-on-strong-pressed);
    }

    &:disabled,
    &[disabled] {
      background-color: var(--cui-bg-accent-strong-disabled);
      border-color: transparent;
      color: var(--cui-fg-on-strong-disabled);
    }
  `;
};
export const secondaryStyles = ({ variant = 'secondary', destructive, }) => {
    if (variant !== 'secondary') {
        return null;
    }
    if (destructive) {
        return css `
      background-color: var(--cui-bg-normal);
      border-color: var(--cui-border-danger);
      color: var(--cui-fg-danger);

      &:hover {
        background-color: var(--cui-bg-normal-hovered);
        border-color: var(--cui-border-danger-hovered);
        color: var(--cui-fg-danger-hovered);
      }

      &:active,
      &[aria-expanded='true'],
      &[aria-pressed='true'] {
        background-color: var(--cui-bg-normal-pressed);
        border-color: var(--cui-border-danger-pressed);
        color: var(--cui-fg-danger-pressed);
      }

      &:disabled,
      &[disabled] {
        background-color: var(--cui-bg-normal-disabled);
        border-color: var(--cui-border-danger-disabled);
        color: var(--cui-fg-danger-disabled);
      }
    `;
    }
    return css `
    background-color: var(--cui-bg-normal);
    border-color: var(--cui-border-normal);
    color: var(--cui-fg-normal);

    &:hover {
      background-color: var(--cui-bg-normal-hovered);
      border-color: var(--cui-border-normal-hovered);
      color: var(--cui-fg-normal-hovered);
    }

    &:active,
    &[aria-expanded='true'],
    &[aria-pressed='true'] {
      background-color: var(--cui-bg-normal-pressed);
      border-color: var(--cui-border-normal-pressed);
      color: var(--cui-fg-normal-pressed);
    }

    &:disabled,
    &[disabled] {
      background-color: var(--cui-bg-normal-disabled);
      border-color: var(--cui-border-normal-disabled);
      color: var(--cui-fg-normal-disabled);
    }
  `;
};
export const tertiaryStyles = ({ variant = 'secondary', destructive, }) => {
    if (variant !== 'tertiary') {
        return null;
    }
    const colorMap = {
        default: {
            idle: 'var(--cui-fg-accent)',
            hovered: 'var(--cui-fg-accent-hovered)',
            pressed: 'var(--cui-fg-accent-pressed)',
            disabled: 'var(--cui-fg-accent-disabled)',
        },
        destructive: {
            idle: 'var(--cui-fg-danger)',
            hovered: 'var(--cui-fg-danger-hovered)',
            pressed: 'var(--cui-fg-danger-pressed)',
            disabled: 'var(--cui-fg-danger-disabled)',
        },
    };
    const colors = destructive ? colorMap.destructive : colorMap.default;
    return css `
    background-color: transparent;
    border-color: transparent;
    color: ${colors.idle};
    padding-left: 0;
    padding-right: 0;

    &:hover {
      color: ${colors.hovered};
      background-color: transparent;
      border-color: transparent;
    }

    &:active,
    &[aria-expanded='true'],
    &[aria-pressed='true'] {
      color: ${colors.pressed};
      background-color: transparent;
      border-color: transparent;
    }

    &:disabled,
    &[disabled] {
      color: ${colors.disabled};
      background-color: transparent;
      border-color: transparent;
    }
  `;
};
const sizeStyles = ({ theme, size = 'giga', }) => {
    const sizeMap = {
        kilo: {
            padding: `calc(${theme.spacings.bit} - ${theme.borderWidth.kilo}) calc(${theme.spacings.mega} - ${theme.borderWidth.kilo})`,
        },
        giga: {
            padding: `calc(${theme.spacings.kilo} - ${theme.borderWidth.kilo}) calc(${theme.spacings.giga} - ${theme.borderWidth.kilo})`,
        },
    };
    return css(Object.assign({}, sizeMap[size]));
};
const stretchStyles = ({ stretch }) => stretch &&
    css `
    width: 100%;
  `;
const loadingStyles = css `
  position: relative;
  overflow: hidden;
`;
const StyledButton = styled('button', {
    shouldForwardProp: (prop) => isPropValid(prop) && prop !== 'size',
})(typography('one'), focusVisible, baseStyles, primaryStyles, secondaryStyles, sizeStyles, tertiaryStyles, stretchStyles, loadingStyles);
/**
 * The Button component enables the user to perform an action or navigate
 * to a different screen.
 */
export const Button = forwardRef((_a, ref) => {
    var { children, disabled, isLoading, loadingLabel, icon: Icon, tracking, as } = _a, props = __rest(_a, ["children", "disabled", "isLoading", "loadingLabel", "icon", "tracking", "as"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        isLoading !== undefined &&
        !loadingLabel) {
        throw new AccessibilityError('Button', "The `loadingLabel` prop is missing. Remove the `isLoading` prop if you don't intend to use the Button's loading state.");
    }
    const { Link } = useComponents();
    const linkOrButton = props.href ? Link : 'button';
    const handleClick = useClickEvent(props.onClick, tracking, 'button');
    return (_jsxs(StyledButton, Object.assign({}, props, (loadingLabel &&
        typeof isLoading === 'boolean' && {
        'aria-live': 'polite',
        'aria-busy': isLoading,
    }), { disabled: disabled || isLoading, ref: ref, as: (as || linkOrButton), onClick: handleClick }, { children: [_jsx(LoadingIcon, Object.assign({ isLoading: Boolean(isLoading), size: "byte" }, { children: _jsx(LoadingLabel, { children: loadingLabel }) })), _jsxs(Content, Object.assign({ isLoading: Boolean(isLoading) }, { children: [Icon && _jsx(Icon, { css: iconStyles, role: "presentation" }), children] }))] })));
});
Button.displayName = 'Button';
